import { Memo } from '@kalos/kalos-rpc';
import {
  Button,
  Card,
  CardContent,
  DataTable,
  DataTableColumnHeader,
  DataTablePagination,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Form,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kalos/ui';
import {
  DownloadIcon,
  EyeOpenIcon,
  Pencil1Icon,
  TrashIcon,
  UploadIcon,
} from '@radix-ui/react-icons';
import { type ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { format, parseISO } from 'date-fns';
import { Loader2, Plus, Sparkles } from 'lucide-react';
import { useMemo } from 'react';

import { DialogOrSheet } from '../../../../../components/DialogOrSheet';
import { LoadErrorState } from '../../../../../components/LoadErrorState';
import { getLoadingColumns } from '../../../../../components/utils';
import { NOTE_REPORT_LIMIT, ROWS_PER_PAGE } from '../../../../../constants';
import { MemoDetailSheet } from '../../../Memo/components/MemoDetailSheet';
import { MemoExport } from '../../../Memo/components/MemoExport';
import { MemoReportForm } from '../../../Memo/components/MemoReportDialog';
import { MemoSummaryList } from '../../../Memo/components/MemoSummaryList';
import {
  MEMO_FILE_TYPE_COLORS,
  MEMO_NOTES_CATEGORY_PILL_CLASS,
  memoStatusPillClass,
} from '../../../Memo/memoTableColumnUtils';
import { Modal } from '../../../Modal';
import { OmniGallery } from '../../../OmniGallery';
import { EditMemoForm } from '../NotesForm/EditMemoForm';
import { AddJobNoteModal } from './AddJobNoteModal';
import { AddMemoFileForm } from './AddMemoFileForm';
import { JobNoteFilters } from './JobNoteFilters';
import { defaultJobNoteFilterValues, jobNoteFilterDimensions } from './jobNoteFiltersUtils';
import { NoteComposer } from './NoteComposer';
import { NotesFeed } from './NotesFeed';
import { useJobNotes } from './useJobNotes';

const staticNotes = Array.from({ length: 5 }, (_, i) => Memo.create({ id: i }));

interface JobNoteListProps {
  jobId?: number;
  projectPrimaryEventId?: number;
  /** When set (e.g. project tab), runs after a note is created instead of the modal default toast */
  onAddNoteSuccess?: () => void | Promise<void>;
  hideSummary?: boolean;
  showExport?: boolean;
  /**
   * Phone layout (NotesFeed): the note composer, then the notes as a feed with a
   * filter sheet. Callers that don't pass it are unchanged.
   */
  mobileLayout?: boolean;
  /** Phone layout: opens a note as its own page. */
  onOpenNote?: (noteId: number) => void;
}

export const JobNoteList = ({
  jobId,
  projectPrimaryEventId,
  onAddNoteSuccess,
  showExport = false,
  mobileLayout = false,
  onOpenNote,
}: JobNoteListProps) => {
  const {
    selectedNote,
    setSelectedNote,
    showFilesModal,
    showUploadModal,
    showDeleteModal,
    setShowDeleteModal,
    submissionLogTab,
    setSubmissionLogTab,
    isReportDialogOpen,
    setIsReportDialogOpen,
    isAddNoteModalOpen,
    setIsAddNoteModalOpen,
    showEditModal,
    showDetailSheet,
    currentNoteIndex,
    isEditingMemo,
    setIsEditingMemo,
    currentPage,
    setCurrentPage,
    isLoadingNextPage,
    sheetCurrentPage,
    sorting,
    filters,
    filterForm,
    handleResetFilters,
    memoSummaryParentDateRange,
    loggedUser,
    isAdmin,
    isCanEditTranscript,
    canEditMemoStatus,
    noteCreatorIds,
    noteEventIds,
    handleMemoSortingChange,
    noteQuery,
    isUpdatingMemo,
    userBatchGetQuery,
    memoStatusQuery,
    memoClassificationQuery,
    handleUploadFiles,
    handleDeleteNote,
    closeFilesModal,
    closeUploadModal,
    handleEditNote,
    handleEditSuccess,
    closeEditModal,
    handleViewDetails,
    closeDetailSheet,
    getFileTypes,
    handleSaveMemoInSheet,
    handleSetState,
    displayNotes,
    hasComments,
    totalNotes,
    isViewingSavedReport,
    setIsViewingSavedReport,
    noteFormJobId,
    reportMemoRequest,
    isGenerateReportDisabled,
    pageCount,
    shouldShowPagination,
    navigateToNote,
    canNavigateNext,
    canNavigatePrev,
  } = useJobNotes({ jobId, projectPrimaryEventId });

  const notesColumns = useMemo<ColumnDef<Memo>[]>(
    () => [
      {
        id: 'row_number',
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle title="#" column={column} />
        ),
        meta: { className: 'w-12 text-center' },
        enableSorting: false,
        cell({ row, column }) {
          const memo = row.original;
          const rowNumber = currentPage * ROWS_PER_PAGE + row.index + 1;
          const fileTypes = getFileTypes(memo.files?.results);
          const hasCommentsValue = hasComments(memo);
          return (
            <div className={column.columnDef.meta?.className}>
              <div className="flex flex-col items-center gap-1">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-200 text-xs font-medium">
                  {rowNumber}
                </div>
                <div className="flex gap-1">
                  {fileTypes.hasAudio && (
                    <span
                      style={{
                        backgroundColor: MEMO_FILE_TYPE_COLORS.audio,
                        height: '8px',
                        width: '8px',
                        borderRadius: '50%',
                        display: 'inline-block',
                      }}
                    />
                  )}
                  {fileTypes.hasPhoto && (
                    <span
                      style={{
                        backgroundColor: MEMO_FILE_TYPE_COLORS.photo,
                        height: '8px',
                        width: '8px',
                        borderRadius: '50%',
                        display: 'inline-block',
                      }}
                    />
                  )}
                  {fileTypes.hasVideo && (
                    <span
                      style={{
                        backgroundColor: MEMO_FILE_TYPE_COLORS.video,
                        height: '8px',
                        width: '8px',
                        borderRadius: '50%',
                        display: 'inline-block',
                      }}
                    />
                  )}
                  {hasCommentsValue && (
                    <span
                      style={{
                        backgroundColor: MEMO_FILE_TYPE_COLORS.comment,
                        height: '8px',
                        width: '8px',
                        borderRadius: '50%',
                        display: 'inline-block',
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'description',
        enableSorting: false,
        meta: { className: 'w-32' },
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle title="Description" column={column} />
        ),
        cell({ row, column }) {
          const fullDescription = row.original.description;
          if (!fullDescription || fullDescription.trim() === '') {
            return <div className={column.columnDef.meta?.className}>-</div>;
          }
          return (
            <div
              role="button"
              tabIndex={0}
              title={fullDescription}
              className={`${column.columnDef.meta?.className} -m-2 cursor-pointer break-words p-2 transition-colors hover:bg-[#f0f6ff] md:-mx-3 md:-my-4 md:px-3 md:py-4`}
              onClick={(e) => {
                e.stopPropagation();
                const idx = displayNotes.findIndex((n) => n.id === row.original.id);
                handleViewDetails(row.original, idx >= 0 ? idx : row.index);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  const idx = displayNotes.findIndex((n) => n.id === row.original.id);
                  handleViewDetails(row.original, idx >= 0 ? idx : row.index);
                }
              }}
            >
              <span className="line-clamp-2 text-blue-600">{fullDescription}</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'classificationId',
        id: 'classification_id',
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle title="Category" column={column} />
        ),
        meta: { className: 'w-24' },
        enableSorting: false,
        cell({ row, column }) {
          const category =
            row.original.classificationId === 0
              ? 'No Category'
              : memoClassificationQuery.data?.results.find(
                  (c) => c.id === row.original.classificationId,
                )?.categoryDescription;
          return (
            <div className={column.columnDef.meta?.className}>
              <span className={MEMO_NOTES_CATEGORY_PILL_CLASS}>{category}</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'created_by',
        id: 'created_by',
        enableSorting: false,
        meta: { className: 'w-24' },
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle title="Created By" column={column} />
        ),
        cell({ row, column }) {
          return (
            <div className={column.columnDef.meta?.className}>{row.original.createdByName}</div>
          );
        },
      },
      {
        accessorKey: 'createdDate',
        id: 'created_date',
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle title="Created Date" column={column} />
        ),
        meta: { className: 'w-20 text-xs' },
        cell({ row, column }) {
          const raw = row.original.createdDate;
          if (!raw) return <span className="text-xs text-gray-500">-</span>;
          const date = parseISO(raw);
          if (isNaN(date.getTime())) return <span className="text-xs text-gray-500">-</span>;
          return (
            <div className={column.columnDef.meta?.className}>
              <div className="flex flex-col">
                <span className="text-sm font-medium">{format(date, 'MM/dd/yyyy')}</span>
                <span className="text-xs text-gray-600">{format(date, 'hh:mm a')}</span>
              </div>
            </div>
          );
        },
      },
      {
        id: 'department',
        accessorKey: 'userDepartmentName',
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle title="Department" column={column} />
        ),
        enableSorting: false,
        meta: { className: 'w-24 text-sm' },
        cell({ row, column }) {
          return (
            <div className={column.columnDef.meta?.className}>
              {row.original.userDepartmentName || 'No Department'}
            </div>
          );
        },
      },
      {
        id: 'status_id',
        accessorKey: 'statusId',
        enableSorting: false,
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle title="Status" column={column} />
        ),
        cell({ row }) {
          const status =
            row.original.statusId === 0
              ? 'No Status'
              : memoStatusQuery.data?.results.find((s) => s.id === row.original.statusId)
                  ?.statusDescription;

          return (
            <div className="w-24">
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${memoStatusPillClass(row.original.statusId)}`}
              >
                {status}
              </span>
            </div>
          );
        },
      },
      {
        id: 'actions',
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle title="Actions" column={column} />
        ),
        meta: { className: 'min-w-[11rem]' },
        cell({ row, column }) {
          const note = row.original;
          const canDelete = isAdmin || note.createdBy === loggedUser.id;
          const noteIndex = displayNotes.findIndex((n) => n.id === note.id);
          return (
            <div className={column.columnDef.meta?.className}>
              <TooltipProvider delayDuration={150}>
                <div className="flex flex-nowrap items-center gap-1">
                  {isAdmin && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-gray-600 hover:bg-gray-200 hover:text-gray-900"
                          onClick={() =>
                            handleViewDetails(note, noteIndex >= 0 ? noteIndex : row.index)
                          }
                        >
                          <EyeOpenIcon className="h-3.5 w-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>View Details (Admin)</TooltipContent>
                    </Tooltip>
                  )}
                  {canDelete && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-gray-600 hover:bg-gray-200 hover:text-gray-900"
                          onClick={() => handleEditNote(note)}
                        >
                          <Pencil1Icon className="h-3.5 w-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Edit Note</TooltipContent>
                    </Tooltip>
                  )}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-gray-600 hover:bg-gray-200 hover:text-gray-900"
                        onClick={() => handleUploadFiles(note)}
                      >
                        <UploadIcon className="h-3.5 w-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Upload Files</TooltipContent>
                  </Tooltip>
                  {canDelete && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-red-600 hover:bg-red-100 hover:text-red-700"
                          onClick={() => {
                            setSelectedNote(note);
                            setShowDeleteModal(true);
                          }}
                        >
                          <TrashIcon className="h-3.5 w-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Delete Note</TooltipContent>
                    </Tooltip>
                  )}
                </div>
              </TooltipProvider>
            </div>
          );
        },
      },
    ],
    [
      currentPage,
      getFileTypes,
      hasComments,
      displayNotes,
      handleViewDetails,
      handleEditNote,
      handleUploadFiles,
      isAdmin,
      loggedUser.id,
      memoClassificationQuery.data?.results,
      memoStatusQuery.data?.results,
      setSelectedNote,
      setShowDeleteModal,
    ],
  );

  const loadingColumns = useMemo(() => getLoadingColumns(notesColumns), [notesColumns]);

  const notesTable = useReactTable({
    data: noteQuery.isPending ? staticNotes : displayNotes,
    getCoreRowModel: getCoreRowModel(),
    columns: noteQuery.isPending ? loadingColumns : notesColumns,
    manualSorting: true,
    onSortingChange: handleMemoSortingChange,
    state: {
      sorting,
      pagination: {
        pageIndex: 0,
        pageSize: ROWS_PER_PAGE,
      },
    },
  });

  if (noteQuery.isError) {
    return mobileLayout ? (
      <LoadErrorState
        title="Couldn't load notes"
        description={`Error loading notes: ${noteQuery.error.message}`}
        retryLabel="Retry loading notes"
        onRetry={() => void noteQuery.refetch()}
        isRetrying={noteQuery.isFetching}
      />
    ) : (
      <div className="p-4 text-red-500">Error loading notes: {noteQuery.error.message}</div>
    );
  }

  const reportDialogContent = (
    <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Generate AI Report</DialogTitle>
      </DialogHeader>
      <MemoReportForm
        memoRequest={reportMemoRequest}
        enabled={isReportDialogOpen}
        onClose={() => setIsReportDialogOpen(false)}
      />
    </DialogContent>
  );

  // Dialogs and sheets for a selected note; both layouts use them.
  const noteModals = (
    <>
      {selectedNote && showFilesModal && (
        <Modal open={showFilesModal} onClose={closeFilesModal}>
          <OmniGallery
            files={selectedNote.files?.results || []}
            title={selectedNote.description}
            readOnly={false}
            allowDelete={true}
            memoId={selectedNote.id}
          />
        </Modal>
      )}

      {selectedNote && showUploadModal && (
        <AddMemoFileForm
          memoId={selectedNote.id}
          isOpen={showUploadModal}
          onClose={closeUploadModal}
        />
      )}

      {selectedNote && showDeleteModal && (
        <DialogOrSheet
          open={showDeleteModal}
          onOpenChange={setShowDeleteModal}
          title="Delete Note"
          description="Are you sure you want to delete this note? This action cannot be undone."
        >
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteNote}>
              Delete
            </Button>
          </div>
        </DialogOrSheet>
      )}

      {selectedNote && showEditModal && (
        <DialogOrSheet
          open={showEditModal}
          onOpenChange={(open) => !open && closeEditModal()}
          title="Edit Note"
          description="Update the note text and adjust pin or internal settings."
          dialogClassName="max-h-[90vh] max-w-[800px] overflow-y-auto"
        >
          <EditMemoForm
            memo={selectedNote}
            onClose={closeEditModal}
            onSuccess={handleEditSuccess}
          />
        </DialogOrSheet>
      )}

      {selectedNote && showDetailSheet && (
        <MemoDetailSheet
          open={showDetailSheet}
          onOpenChange={closeDetailSheet}
          selectedMemo={selectedNote}
          memoFiles={selectedNote.files?.results || []}
          currentMemoIndex={currentNoteIndex}
          sheetCurrentPage={sheetCurrentPage}
          memoTableLength={totalNotes}
          isLoadingNextPage={isLoadingNextPage}
          canNavigateNext={canNavigateNext}
          canNavigatePrev={canNavigatePrev}
          isCanEditTranscript={isCanEditTranscript}
          navigateToMemo={navigateToNote}
          getFileTypes={getFileTypes}
          onEditMemo={() => setIsEditingMemo(true)}
          userBatchGetQuery={userBatchGetQuery}
          memoStatusQuery={memoStatusQuery}
          memoClassificationQuery={memoClassificationQuery}
          isEditingMemo={isEditingMemo}
          setIsEditingMemo={setIsEditingMemo}
          handleSaveMemoInSheet={handleSaveMemoInSheet}
          isUpdatingMemo={isUpdatingMemo}
          handleSetState={handleSetState}
          canEditMemoStatus={canEditMemoStatus}
        />
      )}
    </>
  );

  if (mobileLayout) {
    return (
      <>
        <NotesFeed
          notes={displayNotes}
          isLoading={noteQuery.isPending}
          isFetching={noteQuery.isFetching}
          isError={noteQuery.isError}
          onRetry={() => void noteQuery.refetch()}
          totalNotes={totalNotes}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          statuses={memoStatusQuery.data?.results}
          onOpenNote={(note) => onOpenNote?.(note.id)}
          emptyDescription="Notes added to this project show here."
          composer={
            noteFormJobId ? (
              <NoteComposer
                jobId={noteFormJobId}
                loggedUserId={loggedUser.id}
                categories={memoClassificationQuery.data?.results ?? []}
                onSaved={onAddNoteSuccess}
              />
            ) : (
              <p className="text-muted-foreground text-sm">
                Choose a job in the filters to add a note to it.
              </p>
            )
          }
          filterForm={filterForm}
          searchField="searchText"
          filterDimensions={jobNoteFilterDimensions}
          defaultFilterValues={defaultJobNoteFilterValues}
          renderFilterFields={() => (
            <JobNoteFilters
              inSheet
              availableCreatorIds={noteCreatorIds}
              availableEventIds={noteEventIds}
              showEventIdFilter={!!projectPrimaryEventId}
              // The sheet's own Reset filters control resets the staged fields.
              onReset={() => undefined}
            />
          )}
          onClearFilters={handleResetFilters}
          canGenerateReport={!!(jobId || projectPrimaryEventId) && !isGenerateReportDisabled}
          generateReportDisabledReason={
            totalNotes > NOTE_REPORT_LIMIT
              ? `Too many notes for one report. Narrow your filters (current filter: ${totalNotes} notes).`
              : totalNotes === 0
                ? 'No notes match the current filters.'
                : null
          }
          onGenerateReport={() => setIsReportDialogOpen(true)}
          isViewingReport={isViewingSavedReport}
          reports={
            <MemoSummaryList
              mobileLayout
              onReportOpenChange={setIsViewingSavedReport}
              datesControlledExternally={true}
              primaryEventId={projectPrimaryEventId}
              parentDateRange={memoSummaryParentDateRange}
            />
          }
        />
        <DialogOrSheet
          open={isReportDialogOpen}
          onOpenChange={setIsReportDialogOpen}
          title="Generate AI Report"
        >
          <MemoReportForm
            memoRequest={reportMemoRequest}
            enabled={isReportDialogOpen}
            onClose={() => setIsReportDialogOpen(false)}
          />
        </DialogOrSheet>
        {noteModals}
      </>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter Component - wrapped in Form context */}
      <Form {...filterForm}>
        <JobNoteFilters
          availableCreatorIds={noteCreatorIds}
          availableEventIds={noteEventIds}
          showEventIdFilter={!!projectPrimaryEventId}
          onReset={handleResetFilters}
        />
      </Form>

      <Tabs value={submissionLogTab} onValueChange={setSubmissionLogTab}>
        <div className="text-card-foreground mb-4 flex w-full flex-wrap items-center justify-between gap-4 rounded-xl border bg-white p-4 shadow-sm">
          <TabsList className="bg-muted/40 flex h-max w-fit flex-wrap justify-start">
            <TabsTrigger
              value="Notes"
              className="!rounded-sm first:!rounded-l-sm last:!rounded-r-sm"
            >
              Notes
            </TabsTrigger>
            <TabsTrigger
              value="Reports"
              className="!rounded-sm first:!rounded-l-sm last:!rounded-r-sm"
            >
              Reports
            </TabsTrigger>
          </TabsList>
          {(jobId || projectPrimaryEventId) && (
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <Dialog open={isReportDialogOpen} onOpenChange={setIsReportDialogOpen}>
                <div className="flex shrink-0 items-center gap-2">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipContent>
                        {totalNotes > NOTE_REPORT_LIMIT ? (
                          <>
                            <p>Too many notes.</p>
                            <p>
                              This report is too large to generate in one go. Please narrow your
                              filters (smaller date range or fewer notes) and try again.
                            </p>
                            <p className="mt-1 text-xs">Current filter: {totalNotes} notes</p>
                          </>
                        ) : totalNotes === 0 ? (
                          <>
                            <p>No notes match the current filters.</p>
                            <p>Adjust filters or add notes to generate an AI report.</p>
                          </>
                        ) : (
                          <>
                            <p>AI report</p>
                            <p>
                              Generates a report from the notes currently shown in the list (same
                              filters).
                            </p>
                          </>
                        )}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="gap-2 border-transparent bg-gradient-to-r from-[#ef4444] to-[#ec4899] text-white shadow-sm hover:border-transparent hover:bg-gradient-to-r hover:from-[#ef4444] hover:to-[#ec4899] hover:text-white"
                      disabled={isGenerateReportDisabled}
                    >
                      <Sparkles className="h-4 w-4 shrink-0" />
                      Generate Report
                    </Button>
                  </DialogTrigger>
                </div>
                {reportDialogContent}
              </Dialog>
              <Button
                type="button"
                variant="outline"
                className="gap-2 border-transparent bg-gradient-to-r from-[#ef4444] to-[#ec4899] text-white shadow-sm hover:border-transparent hover:bg-gradient-to-r hover:from-[#ef4444] hover:to-[#ec4899] hover:text-white"
                disabled={!noteFormJobId}
                onClick={() => setIsAddNoteModalOpen(true)}
              >
                <Plus className="h-4 w-4 shrink-0" />
                Add Note
              </Button>
              <AddJobNoteModal
                open={isAddNoteModalOpen}
                onOpenChange={setIsAddNoteModalOpen}
                loggedUserId={loggedUser.id}
                jobId={noteFormJobId}
                onSuccess={onAddNoteSuccess}
              />
            </div>
          )}
        </div>

        <Card className="mt-4 bg-white shadow-sm">
          <CardContent className="p-4">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-xl font-semibold">Submission Log</h2>
              {submissionLogTab === 'Notes' && (
                <div className="flex flex-wrap items-center gap-4">
                  {showExport && jobId && (
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="gap-2">
                          <DownloadIcon />
                          Export
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>Export Job Notes</DialogTitle>
                        </DialogHeader>
                        <MemoExport
                          showFilterForm
                          initialFormValues={{
                            eventId: jobId,
                            startDate: filters.startDate
                              ? format(filters.startDate, 'yyyy-MM-dd')
                              : undefined,
                            endDate: filters.endDate
                              ? format(filters.endDate, 'yyyy-MM-dd')
                              : undefined,
                          }}
                          lockedFields={{ eventId: true }}
                        />
                      </DialogContent>
                    </Dialog>
                  )}
                  {shouldShowPagination && (
                    <DataTablePagination
                      currentPage={currentPage}
                      setPage={setCurrentPage}
                      pageCount={pageCount}
                      disabled={noteQuery.isPending || noteQuery.isFetching}
                    />
                  )}
                  <span className="text-sm text-gray-600">
                    Total: {totalNotes} {totalNotes === 1 ? 'note' : 'notes'}
                  </span>
                </div>
              )}
            </div>
            <TabsContent value="Notes" className="mt-0 focus-visible:outline-none">
              <div className="relative">
                {noteQuery.isFetching && !noteQuery.isPending && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-sm">
                    <div className="flex items-center gap-2 rounded-lg bg-white px-4 py-3 shadow-lg">
                      <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                      <span className="text-sm font-medium text-gray-700">Loading...</span>
                    </div>
                  </div>
                )}
                {!noteQuery.isPending && displayNotes.length === 0 ? (
                  <div className="py-4 text-center text-gray-500">
                    No notes match the selected filters
                  </div>
                ) : (
                  <div className="flex-1 overflow-auto bg-white [&_thead]:bg-gray-50 [&_thead_th>div]:text-[14px] [&_thead_th]:text-[14px] [&_thead_th]:font-medium [&_thead_th]:text-black [&_thead_th_button]:text-[14px] [&_thead_th_button]:text-black">
                    <DataTable table={notesTable} />
                  </div>
                )}
              </div>
            </TabsContent>
            <TabsContent value="Reports" className="mt-0 focus-visible:outline-none">
              <MemoSummaryList
                datesControlledExternally={true}
                primaryEventId={projectPrimaryEventId}
                parentDateRange={memoSummaryParentDateRange}
              />
            </TabsContent>
          </CardContent>
        </Card>
      </Tabs>

      {noteModals}
    </div>
  );
};
